import { useCallback, useEffect, useRef } from 'react';

import { useApolloClient } from '@apollo/client/react';
import { useUser } from '@clerk/react';
import { type AgentEventPayload, WebSocketEntityType } from '@this+that/models';
import log from 'loglevel';

import { useEntitySync } from './useEntitySync';
import { isSuggestionSoundEnabled } from '../../../hooks/SuggestionTabAlerts/isSuggestionSoundEnabled';
import {
  recordTabAlert,
  removeTabAlert,
} from '../../../hooks/SuggestionTabAlerts/recordTabAlert';
import { buildAskAlertKey } from '../../../hooks/SuggestionTabAlerts/suggestionTabAlertsState';
import {
  MY_PENDING_USER_ACTION_CONVERSATION_IDS,
  type MyPendingUserActionConversationIdsResult,
} from '../../../services/agent.service';
import { useAgentConversationsStore } from '../../../store/AgentConversations';
import {
  ConnectionState,
  useWebSocket,
} from '../../WebSocket/WebSocketContext';

function applyPendingFlag(item: AgentEventPayload): void {
  if (item.kind !== 'conversation') {
    return;
  }
  if (typeof item.pendingUserAction !== 'boolean') {
    return;
  }
  useAgentConversationsStore
    .getState()
    .setConversationPendingUserAction(
      item.conversationId,
      item.pendingUserAction,
    );
}

export function useAgentPendingUserActionSync() {
  const client = useApolloClient();
  const { user } = useUser();
  const userId = user?.id;
  const soundEnabled = isSuggestionSoundEnabled(user?.unsafeMetadata);

  const syncTabAlert = useCallback(
    (item: AgentEventPayload) => {
      if (!userId || item.kind !== 'conversation') return;
      const key = buildAskAlertKey(item.conversationId);
      if (item.pendingUserAction === false) {
        void removeTabAlert(userId, key);
        return;
      }
      if (item.pendingUserAction && item.pendingUserActionAt !== undefined) {
        void recordTabAlert({
          userId,
          key,
          createdAt: item.pendingUserActionAt,
          soundEnabled,
        });
      }
    },
    [userId, soundEnabled],
  );

  const { connectionState } = useWebSocket();
  const wasDisconnectedRef = useRef(false);
  const inFlightRef = useRef<Promise<void> | null>(null);

  const fetchPendingIds = useCallback(async () => {
    useAgentConversationsStore.getState().beginPendingUserActionIdsLoad();
    try {
      const { data } =
        await client.query<MyPendingUserActionConversationIdsResult>({
          query: MY_PENDING_USER_ACTION_CONVERSATION_IDS,
          fetchPolicy: 'network-only',
        });
      useAgentConversationsStore
        .getState()
        .setPendingUserActionIds(
          data?.myPendingUserActionConversationIds ?? [],
        );
    } catch (error) {
      log.error('[AgentPendingUserActionSync] Failed to load ids:', error);
      useAgentConversationsStore.setState({ pendingIdsLoadChanges: null });
    }
  }, [client]);

  // Overlapping triggers (mount, socket reconnect, long-pause event) share one load: a second
  // beginPendingUserActionIdsLoad would discard the live changes the first one is collecting.
  const loadPendingIds = useCallback((): Promise<void> => {
    if (!inFlightRef.current) {
      inFlightRef.current = fetchPendingIds().finally(() => {
        inFlightRef.current = null;
      });
    }
    return inFlightRef.current;
  }, [fetchPendingIds]);

  useEffect(() => {
    loadPendingIds();
  }, [loadPendingIds]);

  useEffect(() => {
    if (connectionState !== ConnectionState.CONNECTED) {
      wasDisconnectedRef.current = true;
      return;
    }
    if (wasDisconnectedRef.current) {
      wasDisconnectedRef.current = false;
      loadPendingIds();
    }
  }, [connectionState, loadPendingIds]);

  useEntitySync<AgentEventPayload>(WebSocketEntityType.AgentEvent, {
    onInsert: (item) => {
      applyPendingFlag(item);
      syncTabAlert(item);
    },
    onModify: (item) => {
      applyPendingFlag(item);
      syncTabAlert(item);
    },
    onReconnect: loadPendingIds,
  });
}
